import {useQuery} from '@tanstack/react-query';
import api from '@/api';
import {Deliverable, UploadCounts} from '@/views/gig-deliverables/types';
import {GigShellData} from '@/views/gig-deliverables/shell/shellTypes';
import {
  GigAffiliateData,
  GigApplicationData,
  GigBriefData,
} from '@/views/gig-deliverables/shell/informationTypes';
import {GigPrepData} from '@/views/gig-deliverables/shell/prepTypes';
import networkDisplayName from '@/views/gig-deliverables/shell/networkName';
import {SocialPostGigData} from '@/api/socialPosts/types';

// Mirrors the Ember STATE_TO_SCOPE_MAP in paid-gig.js
const STATE_TO_SCOPE: Record<string, string> = {
  proposed: 'pending',
  awaiting_preapproval: 'pending',
  in_negotiation: 'pending',
  awaiting_influencer_uploads: 'active',
  awaiting_brand_uploads_feedback: 'active',
  awaiting_influencer_posts: 'active',
  awaiting_payment: 'active',
  completed: 'completed',
  rejected: 'archive',
  expired: 'archive',
  cancelled: 'archive',
};

export function paidGigDetailQueryKey(gigId: string) {
  return ['paid-gig-detail', gigId];
}

const SUPPORT_MERCHANT_NAME = 'Popular Pays';

function mapShell(gig: any): GigShellData {
  const state: string = gig?.state ?? '';
  const conversations: any[] = gig?.conversations ?? [];
  const brandConversationId: string | null = gig?.conversation?.id ?? null;
  const support = conversations.find(
    (c) =>
      c?.id !== brandConversationId &&
      c?.merchant?.name === SUPPORT_MERCHANT_NAME
  );
  const conversationId: string | null =
    brandConversationId ?? conversations[0]?.id ?? null;
  const supportConversationId: string | null = support?.id ?? null;
  return {
    campaignId: String(gig?.campaign?.id ?? ''),
    campaignName: gig?.campaignName ?? '',
    merchantName: gig?.campaign?.merchant?.name ?? '',
    imageUrl: gig?.campaignImageUrl ?? null,
    state,
    socialNetworkName: networkDisplayName(
      gig?.socialNetwork,
      gig?.campaign?.network
    ),
    gigScope: STATE_TO_SCOPE[state] ?? 'pending',
    replacedByGigId: gig?.replacedBy?.id ?? null,
    hasMessages: conversationId != null,
    hasUnreadConversations: gig?.hasUnreadConversations ?? false,
    conversationId,
    supportConversationId,
    commissionRate: gig?.campaign?.commissionRate ?? null,
    hasShipment: gig?.shipment != null,
    hasGiftCode: gig?.giftCode != null,
    counts: {
      photo: {
        required: gig?.photosToUpload ?? 0,
        accepted: gig?.acceptedPhotoSubmissionsCount ?? 0,
        rejected: gig?.rejectedPhotoSubmissionsCount ?? 0,
      },
      video: {
        required: gig?.videosToUpload ?? 0,
        accepted: gig?.acceptedVideoSubmissionsCount ?? 0,
        rejected: gig?.rejectedVideoSubmissionsCount ?? 0,
      },
      blog: {
        required: gig?.blogsToUpload ?? 0,
        accepted: gig?.acceptedBlogSubmissionsCount ?? 0,
        rejected: gig?.rejectedBlogSubmissionsCount ?? 0,
      },
      mixedMedia: {
        required: gig?.mixedMediaToUpload ?? 0,
        accepted: gig?.acceptedMixedMediaSubmissionsCount ?? 0,
        rejected: gig?.rejectedMixedMediaSubmissionsCount ?? 0,
      },
      feedPosts: {
        required: gig?.requiredFeedPostsCount ?? 0,
        accepted: gig?.acceptedFeedPostsCount ?? 0,
      },
      reels: {
        required: gig?.requiredReelsPostsCount ?? 0,
        accepted: gig?.acceptedReelsPostsCount ?? 0,
      },
      stories: {
        required: gig?.requiredStoryPostsCount ?? 0,
        accepted: gig?.acceptedStoryPostsCount ?? 0,
      },
    },
  };
}

function mapPrep(gig: any): GigPrepData {
  const shipment = gig?.shipment;
  const giftCode = gig?.giftCode;

  return {
    shipment: shipment
      ? {
          state: shipment.state ?? '',
          carrier: shipment.carrier ?? null,
          trackingCode: shipment.trackingCode ?? null,
          trackingCodeUrl: shipment.trackingCodeUrl ?? null,
        }
      : null,
    giftCode: giftCode
      ? {
          state: giftCode.state ?? '',
          couponCode: giftCode.couponCode ?? null,
        }
      : null,
  };
}

// States where a payment message is meaningful — Ember gig-page/proposal.js shouldShowPaymentMessage
const PAYMENT_MESSAGE_STATES = new Set(['awaiting_payment', 'completed']);

/** Allowlist, not a passthrough of `gig.campaign` — see informationTypes.ts. */
function mapBrief(gig: any): GigBriefData {
  const campaign = gig?.campaign;
  // Ember brief.hbs:36 — `and model.shopifyDiscountCode model.campaign.hasAffiliate`,
  // where campaign.js hasAffiliate is `notEmpty('affiliateType')`.
  const hasAffiliate = Boolean(campaign?.affiliateType);
  const code = gig?.shopifyDiscountCode?.code ?? null;

  return {
    eligibility: {
      network: campaign?.network ?? null,
      unlocksAt: campaign?.unlocksAt ?? null,
    },
    preferredCreator: {
      gender: campaign?.preferredCreatorProfile?.genderDescription ?? null,
      age: campaign?.preferredCreatorProfile?.ageDescription ?? null,
      location: campaign?.preferredCreatorProfile?.locationDescription ?? null,
      specialty:
        campaign?.preferredCreatorProfile?.specialtyDescription ?? null,
      capability:
        campaign?.preferredCreatorProfile?.capabilityDescription ?? null,
    },
    creatorIsInTheZone: Boolean(campaign?.creatorInTheZone),
    descriptionHtml: campaign?.descriptionHtml ?? null,
    // Ember brief-item-promotable-url.js isShown — gated on the *campaign* defining
    // tracking URLs, so the placeholder copy still shows before the gig is accepted.
    showPromotableUrls: (campaign?.campaignTrackingUrls?.length ?? 0) > 0,
    promotableLinks: (gig?.promotableLinks ?? [])
      .filter((link: any) => link?.trackingUrl)
      .map((link: any) => ({
        id: String(link.id),
        label: link.label ?? null,
        trackingUrl: link.trackingUrl,
      })),
    discountCode: hasAffiliate ? code : null,
    briefItems: (gig?.briefItems ?? [])
      .filter((item: any) => item?.contentHtml || item?.highlighted)
      .sort((a: any, b: any) => (a.weight ?? 0) - (b.weight ?? 0))
      .map((item: any) => ({
        id: String(item.id),
        title: item.title ?? '',
        contentHtml: item.contentHtml ?? null,
        highlighted: item.highlighted ?? null,
      })),
  };
}

function mapApplication(gig: any): GigApplicationData {
  const state: string = gig?.state ?? '';
  const dueOn = gig?.paidGigPayments?.[0]?.dueOn ?? null;

  return {
    createdAt: gig?.createdAt ?? null,
    deadline: gig?.deadline ?? null,
    cost: gig?.cost ?? null,
    usagePeriodMonths:
      gig?.negotiatedContentAgreementTimePeriodInMonths ?? null,
    // Answers are polymorphic; only TextAnswer carries `body`. Ember's
    // `submittedValue` is defined on text-answer alone, so other kinds render
    // an empty value there too.
    answers: (gig?.answers ?? [])
      .filter((answer: any) => answer?.question?.body)
      .map((answer: any) => ({
        id: String(answer.id),
        questionHtml: answer.question.body,
        value: answer.body ?? '',
      })),
    state,
    paymentDueOn: PAYMENT_MESSAGE_STATES.has(state) ? dueOn : null,
  };
}

function latestProcessedOrderAt(gig: any): string | null {
  let latest: string | null = null;
  let latestMs = -Infinity;

  (gig?.processedOrders ?? []).forEach((order: any) => {
    const updatedAt = order?.updatedAt;
    const ms = updatedAt ? Date.parse(updatedAt) : NaN;
    if (!Number.isNaN(ms) && ms > latestMs) {
      latestMs = ms;
      latest = updatedAt;
    }
  });

  return latest;
}

function mapAffiliate(gig: any): GigAffiliateData {
  const discountCode = gig?.shopifyDiscountCode;
  const discount = discountCode?.shopifyDiscount;

  return {
    discountCode: discountCode?.code ?? null,
    discountValue: discount ? Number(discount.value ?? 0) : null,
    discountValueType: discount?.valueType ?? null,
    expiresAt: discount?.endsAt ?? null,
    usageCount: discountCode?.usageCount ?? null,
    lastProcessedOrderAt: latestProcessedOrderAt(gig),
    commissionIncomeUsd: gig?.affiliateCommissionIncomeUsd ?? null,
  };
}

function mapSocialPosts(gig: any): SocialPostGigData {
  const order = [...(gig?.orders ?? [])].sort((a: any, b: any) => {
    const aTime = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
    const bTime = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
    return aTime - bTime;
  })[0];

  return {
    identityId: gig?.identityId ?? gig?.socialNetwork?.identity?.id ?? null,
    socialNetworkId: gig?.socialNetwork?.id ?? null,
    socialNetworkKey:
      gig?.socialNetwork?.name ?? gig?.campaign?.network ?? null,
    tiktokOrder: order
      ? {
          id: String(order.id),
          orderType: order.orderType ?? null,
          orderStatus: order.orderStatus ?? null,
          tiktokOrderId: order.tiktokOrderId ?? null,
          inviteLink: order.tcmCampaign?.inviteLink ?? null,
        }
      : null,
  };
}

/**
 * AMS emits linkage for every relationship (`include_data_default = true`), so one
 * resource hangs off several parents and jsonapi-react inlines it under each. The
 * duplicates are harmless to read but are written out in full when React Query
 * persists the cache — measured at ~13x the payload for a 12-item brief. Nothing
 * reads the keys dropped here; the surviving path is noted per line.
 */
function dropDuplicateEdges<T>(payload: T): T {
  const gig: any = (payload as any)?.data;
  if (!gig) return payload;

  (gig.briefItems ?? []).forEach((item: any) => {
    delete item?.campaign; // gig.campaign
  });
  (gig.paidGigPayments ?? []).forEach((payment: any) => {
    delete payment?.campaign; // gig.campaign
    delete payment?.paidGig; // the gig itself
  });
  if (gig.campaign) {
    delete gig.campaign.campaignPaidGigBriefItems; // gig.briefItems
    delete gig.campaign.questions; // gig.answers[].question
  }
  if (gig.processedOrders) {
    gig.processedOrders = gig.processedOrders.map((order: any) => ({
      id: order?.id,
      updatedAt: order?.updatedAt ?? null,
    }));
  }
  if (gig.shopifyDiscountCode?.shopifyDiscount) {
    delete gig.shopifyDiscountCode.shopifyDiscount.shopifyDiscountCodes;
  }
  // The `conversation` alias duplicates a record in `conversations`, so drop its heavy
  // fields — but keep the id: it authoritatively marks the brand (merchant) thread, which
  // the `conversations` array order does not (mapShell needs it to place the support thread).
  if (gig.conversation) {
    gig.conversation = {id: gig.conversation.id};
  }

  return payload;
}

export default function usePaidGigDetailQuery(gigId: string) {
  const {data, isLoading, isError, error, refetch} = useQuery({
    queryKey: paidGigDetailQueryKey(gigId),
    queryFn: async () =>
      dropDuplicateEdges(
        await api.get(`/paid_gigs/${gigId}`, {
          params: {
            // Mirrors the Ember gig route's include list (routes/protected/verified/
            // gigs/gig.js). jsonapi-react resolves relationships against `included`
            // only, so anything omitted here comes back empty rather than lazy-loaded.
            include: [
              'answers.question',
              'brief_items',
              'campaign',
              'campaign.campaign_tracking_urls',
              'campaign.merchant',
              'campaign.preferred_creator_profile',
              'conversation',
              'conversations.merchant',
              'deliverables',
              'gift_code',
              'paid_gig_payments',
              'processed_orders',
              'promotable_links',
              'replaced_by',
              'shipment',
              'shopify_discount_code',
              'shopify_discount_code.shopify_discount',
              'social_network.identity',
              'orders.tcm_campaign',
            ],
          },
        })
      ),
    enabled: Boolean(gigId),
    refetchOnWindowFocus: false,
  });
  const gig: any = data?.data;
  const uploadCounts: UploadCounts = {
    photo: gig?.photosToUpload ?? 0,
    video: gig?.videosToUpload ?? 0,
    blog: gig?.blogsToUpload ?? 0,
    mixed_media: gig?.mixedMediaToUpload ?? 0,
  };
  return {
    gig,
    deliverables: (gig?.deliverables ?? []) as Deliverable[],
    uploadCounts,
    campaignNetwork: gig?.campaign?.network ?? null,
    shell: mapShell(gig),
    brief: mapBrief(gig),
    application: mapApplication(gig),
    prep: mapPrep(gig),
    affiliate: mapAffiliate(gig),
    socialPosts: mapSocialPosts(gig),
    isLoading,
    isError,
    error,
    refetch,
  };
}
